import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import SEOHead from '../../components/SEO/SEOHead';
import SEOFooter from '../../components/SEO/SEOFooter';
import {
  ArrowLeft, Brain, User, Target, Clock, BarChart3, CheckCircle2
} from 'lucide-react';

const AIvsHumanPicks = () => {
  const navigate = useNavigate();

  const comparisonMetrics = [
    {
      metric: 'Processing Speed',
      ai: '1000+ data points in seconds',
      human: 'Limited by time & attention',
      winner: 'ai'
    },
    {
      metric: 'Emotional Bias',
      ai: 'Zero emotion, pure data',
      human: 'Influenced by favorites, recency bias',
      winner: 'ai'
    },
    {
      metric: 'Consistency',
      ai: 'Same methodology 24/7',
      human: 'Varies based on mood, fatigue',
      winner: 'ai'
    },
    {
      metric: 'Pattern Recognition',
      ai: 'Finds hidden correlations in millions of games',
      human: 'Limited to recent memory',
      winner: 'ai'
    },
    {
      metric: 'Intangibles',
      ai: 'Can incorporate via sentiment analysis',
      human: 'Better at reading "feel" of games',
      winner: 'human'
    },
    {
      metric: 'Adaptation',
      ai: 'Learns from every result instantly',
      human: 'Slower to adjust strategies',
      winner: 'ai'
    },
    {
      metric: 'Real-Time Updates',
      ai: 'Instant injury/lineup adjustments',
      human: 'May miss breaking news',
      winner: 'ai'
    },
    {
      metric: 'Bankroll Management',
      ai: 'Mathematically optimal bet sizing',
      human: 'Prone to emotional over-betting',
      winner: 'ai'
    }
  ];

  const performanceData = [
    { sport: 'NFL', ai: '69%', human: '55%', difference: '+14%' },
    { sport: 'NBA', ai: '72%', human: '53%', difference: '+19%' },
    { sport: 'MLB', ai: '65-68%', human: '51%', difference: '+17%' },
    { sport: 'Soccer', ai: '72-78%', human: '54%', difference: '+24%' }
  ];

  const bestApproach = [
    {
      title: 'Use PickStar for Data Analysis',
      description: 'Let PickStar crunch numbers, analyze stats, and identify value bets'
    },
    {
      title: 'Apply Human Context',
      description: 'Add your sports knowledge for situational factors PickStar might miss'
    },
    {
      title: 'Follow AI Bankroll Rules',
      description: 'Stick to AI\'s mathematically optimal bet sizing recommendations'
    },
    {
      title: 'Track Both',
      description: 'Compare PickStar picks vs your intuition to improve over time'
    }
  ];

  const faqs = [
    {
      q: "Which wins more often: AI picks or human picks?",
      a: "Studies show AI picks average 63-68% accuracy vs 51-55% for human experts. PickStar's +edge comes from processing vastly more data without emotional bias. PickStar AI specifically maintains 65%+ win rates across major sports."
    },
    {
      q: "Can humans ever beat AI at sports betting?",
      a: "In very specific situations involving intangibles (team chemistry changes, locker room issues), humans might have an edge. However, for consistent long-term profits, AI's data processing power and emotion-free decision-making win overwhelmingly."
    },
    {
      q: "Should I ignore my own research and only use AI?",
      a: "No. The best approach combines both: use PickStar for data-heavy analysis and optimal bet sizing, then apply your sports knowledge for context. PickStar + Human expertise beats either alone."
    },
    {
      q: "Why do human experts lose to AI?",
      a: "Humans suffer from recency bias, emotional attachment to teams, and limited data processing. We can only analyze a fraction of what AI can, and our emotions often override logic. AI has none of these limitations."
    },
    {
      q: "How does PickStar AI compare to professional handicappers?",
      a: "PickStar AI outperforms most professional handicappers because we process millions of data points they can't access, update predictions in real-time, and never chase losses emotionally. Our 65%+ win rate beats industry averages."
    }
  ];

  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "AI vs Human Sports Picks: Which Wins More Often?",
    "description": "Data-driven comparison of AI sports betting picks vs human experts. See which approach wins more consistently in NFL, NBA, MLB, and Soccer betting.",
    "author": {
      "@type": "Organization",
      "name": "PickStar AI"
    },
    "publisher": {
      "@type": "Organization",
      "name": "PickStar AI",
      "logo": {
        "@type": "ImageObject",
        "url": "https://pickstarai.com/logo.png"
      }
    },
    "datePublished": "2025-10-23",
    "dateModified": new Date().toISOString(),
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://pickstarai.com/ai-vs-human-picks"
    }
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  return (
    <>
      <SEOHead
        title="AI vs Human Sports Picks: Which Wins More Often? | Betting Accuracy Comparison"
        description="AI sports picks vs human experts: See the data. AI averages 65%+ win rate vs 53% for humans. Compare accuracy, speed, and consistency in NFL, NBA, MLB, Soccer betting."
        keywords="ai picks vs human, ai betting accuracy, human vs ai sports picks, ai sports betting comparison, betting ai vs experts, sports prediction ai, human handicapper vs ai"
        canonical="https://pickstarai.com/ai-vs-human-picks"
        ogImage="https://pickstarai.com/og-home-logo.png"
        structuredData={[structuredData, faqStructuredData]}
      />

      <div className="min-h-screen bg-black">
        {/* Terminal Header */}
        <header className="fixed top-0 z-50 w-full bg-black border-b border-white/[0.05]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-14">
              <Link
                to="/"
                className="flex items-center text-white/60 hover:text-white/90 transition-colors text-[11px] uppercase tracking-wider"
              >
                <ArrowLeft className="w-3.5 h-3.5 mr-2" />
                Home
              </Link>
              <div className="text-[9px] text-white/40 uppercase tracking-widest font-mono">
                UPDATED: {new Date().toLocaleDateString('en-US').toUpperCase()}
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section - Terminal */}
        <div className="pt-14 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent pointer-events-none" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 relative">
            <div className="text-center">
              <div className="flex items-center justify-center gap-6 mb-8">
                <Brain className="w-16 h-16 text-blue-400" />
                <span className="text-3xl font-mono font-bold text-white/40">VS</span>
                <User className="w-16 h-16 text-violet-400" />
              </div>
              <div className="text-[9px] text-emerald-400/60 uppercase tracking-[0.2em] font-mono mb-4">
                Data-Driven Comparison
              </div>
              <h1 className="text-5xl lg:text-7xl font-thin text-white/95 tracking-tight mb-6">
                AI vs Human Sports Picks
              </h1>
              <p className="text-base lg:text-lg text-white/50 max-w-2xl mx-auto leading-relaxed">
                The data is clear: AI beats human experts. See the head-to-head comparison.
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-16">

          {/* Win Rate Comparison */}
          <section className="mb-16">
            <div className="text-[10px] text-emerald-400/60 uppercase tracking-[0.2em] font-mono mb-4 text-center">
              Performance Data
            </div>
            <h2 className="text-3xl font-thin text-white/90 mb-12 text-center">
              Win Rate Comparison by Sport
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {performanceData.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/[0.02] border border-white/[0.05] rounded-lg p-6 hover:border-white/10 transition-all"
                >
                  <h3 className="text-xl font-semibold text-white/90 mb-6 text-center">
                    {item.sport}
                  </h3>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Brain className="w-5 h-5 text-blue-400" />
                        <span className="text-sm text-white/50">PickStar AI</span>
                      </div>
                      <span className="text-2xl font-mono font-bold text-blue-400">{item.ai}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <User className="w-5 h-5 text-violet-400" />
                        <span className="text-sm text-white/50">Human</span>
                      </div>
                      <span className="text-2xl font-mono font-bold text-violet-400">{item.human}</span>
                    </div>

                    <div className="pt-4 border-t border-white/[0.05] text-center">
                      <span className="text-xs text-white/40 uppercase tracking-wider">AI Advantage</span>
                      <div className="text-xl font-mono font-bold text-emerald-400 mt-1">{item.difference}</div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Detailed Comparison Table */}
          <section className="mb-16">
            <div className="text-[10px] text-emerald-400/60 uppercase tracking-[0.2em] font-mono mb-4">
              Detailed Analysis
            </div>
            <h2 className="text-3xl font-thin text-white/90 mb-8">
              Head-to-Head Comparison
            </h2>

            <div className="bg-white/[0.02] border border-white/[0.05] rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-white/[0.02] border-b border-white/[0.05]">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-white/90 uppercase tracking-wider">Metric</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-white/90 uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <Brain className="w-5 h-5 text-blue-400" />
                        AI Picks
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-white/90 uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <User className="w-5 h-5 text-violet-400" />
                        Human Experts
                      </div>
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-white/90 uppercase tracking-wider">Winner</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.05]">
                  {comparisonMetrics.map((item, index) => (
                    <tr key={index} className="hover:bg-white/[0.02] transition-colors">
                      <td className="px-6 py-4 text-sm font-medium text-white/80">
                        {item.metric}
                      </td>
                      <td className="px-6 py-4 text-sm text-white/50">
                        {item.ai}
                      </td>
                      <td className="px-6 py-4 text-sm text-white/50">
                        {item.human}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {item.winner === 'ai' ? (
                          <Brain className="w-6 h-6 text-blue-400 mx-auto" />
                        ) : (
                          <User className="w-6 h-6 text-violet-400 mx-auto" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Best Approach */}
          <section className="mb-16 bg-gradient-to-r from-blue-500/10 to-violet-500/10 border border-white/[0.05] rounded-lg p-8 md:p-12">
            <div className="text-[10px] text-emerald-400/60 uppercase tracking-[0.2em] font-mono mb-4 text-center">
              Optimal Strategy
            </div>
            <h2 className="text-3xl font-thin text-white/90 mb-6 text-center">
              The Best Approach: AI + Human
            </h2>

            <p className="text-center text-white/50 mb-10 max-w-2xl mx-auto">
              While AI clearly outperforms humans, the optimal strategy combines both strengths.
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              {bestApproach.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/[0.02] border border-white/[0.05] rounded-lg p-6 hover:border-white/10 transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-emerald-500/20 rounded-full p-2 flex-shrink-0">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white/90 mb-2">
                        {item.title}
                      </h3>
                      <p className="text-white/50 text-sm">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Why AI Wins */}
          <section className="mb-16">
            <div className="text-[10px] text-emerald-400/60 uppercase tracking-[0.2em] font-mono mb-4">
              Key Advantages
            </div>
            <h2 className="text-3xl font-thin text-white/90 mb-8">
              Why AI Consistently Beats Humans
            </h2>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white/[0.02] border border-white/[0.05] rounded-lg p-6 hover:border-white/10 transition-all">
                <Clock className="w-10 h-10 text-blue-400 mb-4" />
                <h3 className="text-lg font-semibold text-white/90 mb-3">
                  Speed & Scale
                </h3>
                <p className="text-white/50 text-sm">
                  AI analyzes 1000+ data points per game in seconds. Humans can't physically process this volume of information.
                </p>
              </div>

              <div className="bg-white/[0.02] border border-white/[0.05] rounded-lg p-6 hover:border-white/10 transition-all">
                <Target className="w-10 h-10 text-blue-400 mb-4" />
                <h3 className="text-lg font-semibold text-white/90 mb-3">
                  Zero Bias
                </h3>
                <p className="text-white/50 text-sm">
                  AI doesn't have favorite teams, doesn't tilt after losses, and makes every decision based purely on data.
                </p>
              </div>

              <div className="bg-white/[0.02] border border-white/[0.05] rounded-lg p-6 hover:border-white/10 transition-all">
                <BarChart3 className="w-10 h-10 text-blue-400 mb-4" />
                <h3 className="text-lg font-semibold text-white/90 mb-3">
                  Pattern Recognition
                </h3>
                <p className="text-white/50 text-sm">
                  AI identifies hidden correlations across millions of historical games that humans would never notice.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="mb-16">
            <div className="text-[10px] text-emerald-400/60 uppercase tracking-[0.2em] font-mono mb-4">
              Common Questions
            </div>
            <h2 className="text-3xl font-thin text-white/90 mb-8">
              Frequently Asked Questions
            </h2>

            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <details
                  key={index}
                  className="bg-white/[0.02] border border-white/[0.05] rounded-lg overflow-hidden group hover:border-white/10 transition-all"
                >
                  <summary className="px-6 py-4 cursor-pointer list-none flex items-center justify-between text-white/80 hover:text-white/95 transition-colors">
                    <span className="text-sm font-medium pr-4">{faq.q}</span>
                    <span className="text-white/40 group-open:rotate-180 transition-transform">▼</span>
                  </summary>
                  <div className="px-6 pb-4 text-sm text-white/50 leading-relaxed border-t border-white/[0.05] pt-4">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* Bottom CTA */}
          <div className="mt-20 text-center">
            <div className="bg-white/[0.02] border border-white/[0.05] rounded-lg p-12">
              <div className="text-[10px] text-emerald-400/60 uppercase tracking-[0.2em] font-mono mb-4">
                Get Started
              </div>
              <h2 className="text-3xl font-thin text-white/90 mb-4">
                Try AI Picks Risk-Free
              </h2>
              <p className="text-base text-white/50 mb-8 max-w-2xl mx-auto leading-relaxed">
                See why thousands of smart bettors switched from human experts to PickStar AI. Start with free picks today.
              </p>
              <button
                onClick={() => navigate('/chat')}
                className="px-8 py-3 bg-gradient-to-r from-emerald-500/80 to-violet-500/80 hover:from-emerald-500 hover:to-violet-500 text-white text-sm font-medium rounded-lg transition-all shadow-lg shadow-emerald-500/20"
              >
                Get Free AI Picks
              </button>
            </div>
          </div>
        </div>

        {/* SEO Footer */}
        <SEOFooter language="en" />
      </div>
    </>
  );
};

export default AIvsHumanPicks;
